import React from "react";
import { cn } from "@/lib/utils";
import { cva } from "class-variance-authority";
import { AssetRoutes } from "@/lib/routes";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";

const userAvatarVariants = cva("rounded-md", {
  variants: {
    size: {
      "3xl": "h-16 w-16",
      "2xl": "h-12 w-12",
      default: "h-8 w-8",
      lg: "h-7 w-7",
      md: "h-6 w-6",
      sm: "rounded-full h-5 w-5",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

const UserAvatar = React.forwardRef(
  ({ user, value, size, className, coinOnly, onClick, secondaryText = null, emptyUser = "Unassigned" }, ref) => {
    user = user ?? value;
    return (
      <div className={cn("flex items-center gap-2.5", onClick && "cursor-pointer", className)} onClick={onClick}>
        <Avatar className={cn(userAvatarVariants({ size }))} ref={ref}>
          {user && <AvatarImage src={AssetRoutes.forAvatar(user.id)} />}
          <AvatarFallback
            className={cn(
              "rounded-sm tracking-normal",
              user ? "text-[10px]" : "text-[11px]",
              user && "text-white",
              user ? generateColour(user?.name) : "bg-input/60"
            )}
          >
            {user ? getInitials(user.name) : "?"}
          </AvatarFallback>
        </Avatar>
        {!coinOnly && (
          <div className="flex flex-col justify-center">
            {onClick ? (
              <Button
                variant="link"
                onClick={(e) => { e.stopPropagation(); onClick(e); }}
                className="h-auto truncate text-nowrap px-0 font-normal text-inherit"
              >
                {user?.name || emptyUser}
              </Button>
            ) : (
              <span className="truncate text-nowrap">{user?.name || emptyUser}</span>
            )}
            {secondaryText && <span className="text-xs text-muted-foreground">{secondaryText}</span>}
          </div>
        )}
      </div>
    );
  }
);

UserAvatar.displayName = "UserAvatar";

export default UserAvatar;

export function getInitials(name) {
  if (!name) {
    return "";
  }

  const reducedName = name
    .replace(/\s+/g, " ")
    .replace(/\s*\(.*?\)\s*/g, " ")
    .replace(/\s*{.*?}\s*/g, " ")
    .replace(/\s*\[.*?]\s*/g, " ");

  const initials = reducedName
    .split(" ")
    .filter((item) => item !== "")
    .map((item) => item.charAt(0))
    .reduce((accumulator, currentValue) => accumulator + currentValue, "");

  if (initials.length > 2) {
    return initials.charAt(0) + initials.charAt(initials.length - 1);
  }

  return initials;
}

const colours = [
  "bg-red-400",
  "bg-green-400",
  "bg-blue-400",
  "bg-orange-400",
  "bg-indigo-400",
  "bg-cyan-400",
  "bg-emerald-400",
  "bg-yellow-400",
  "bg-amber-400",
  "bg-pink-400",
  //"bg-lime-400",
];

export const generateColour = (name) => {
  let sum = 0;
  for (let i = 0; i < name.length; i++) {
    sum += name.charCodeAt(i);
  }
  return colours[sum % colours.length];
};
